import type { Ref, ResolvedState } from '@/api/types'
import { stateTone } from '@/domain/track'
import { cotalkerChannelUrl } from '@/config'
import { Icon } from './Icons'

export type Tone = 'red' | 'orange' | 'amber' | 'blue' | 'slate' | 'green'

export function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`pill pill--${tone}`}>
      <span className="dot" />
      {children}
    </span>
  )
}

const PRIORITY_TONE: Record<string, Tone> = {
  alta: 'red',
  media_alta: 'orange',
  media: 'amber',
  baja: 'slate',
}

export function PriorityPill({ priority }: { priority: Ref }) {
  if (!priority.display || priority.display === '—') return null
  const key = priority.code ?? priority.display.toLowerCase().replace(/\s+/g, '_')
  return <Pill tone={PRIORITY_TONE[key] ?? 'slate'}>{priority.display}</Pill>
}

export function StateBadge({ state }: { state: ResolvedState }) {
  const text = state.unknown
    ? 'Estado no disponible'
    : state.isBranch
      ? (state.branchLabel ?? state.label)
      : state.label
  return <Pill tone={stateTone(state)}>{text}</Pill>
}

/**
 * Abre la tarea en Cotalker por su canal: /channel/{task.channel}. Vale igual
 * para una incidencia y para una OT. `channel` es opcional, así que si falta
 * no se dibuja nada — un enlace roto es peor que ninguno.
 */
export function CotalkerLink({ channel, what }: { channel: string | null; what: string }) {
  const url = cotalkerChannelUrl(channel)
  if (!url) return null
  const label = `Abrir ${what} en Cotalker`
  return (
    <a
      className="cot-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
    >
      <Icon name="external" size={14} />
      <span className="cot-txt">Ver en Cotalker</span>
    </a>
  )
}

/**
 * Botón de actualizar de la cabecera de una sección.
 *
 * El listado se carga al abrir el portal y el portal queda abierto toda la
 * jornada: sin esto, la única forma de ver un estado nuevo es recargar la
 * página. No tiene nada que ver con el scroll infinito, que sigue trayendo
 * páginas solo — este relee lo que ya está.
 */
export function RefreshButton({
  onClick,
  busy,
  label,
}: {
  onClick: () => void
  busy: boolean
  label: string
}) {
  return (
    <button
      type="button"
      className={`hd-btn${busy ? ' busy' : ''}`}
      onClick={onClick}
      disabled={busy}
      aria-label={label}
      title={label}
    >
      <Icon name="refresh" size={15} />
    </button>
  )
}

export function Fact({ label, value, numeric }: { label: string; value: string; numeric?: boolean }) {
  return (
    <div className="fact">
      <p className="k">{label}</p>
      <p className={`v${numeric ? ' num' : ''}`}>{value}</p>
    </div>
  )
}

export function CardSkeleton() {
  return (
    <div className="sk" aria-hidden="true">
      <i />
      <i />
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="card" role="alert">
      <p style={{ fontWeight: 650, color: 'var(--ink)' }}>No se pudo cargar</p>
      <p style={{ color: 'var(--ink-2)', marginTop: 4 }}>{message}</p>
      <button type="button" className="btn" style={{ marginTop: 14 }} onClick={onRetry}>
        <Icon name="refresh" size={15} />
        Reintentar
      </button>
    </div>
  )
}

export function EmptyIncidents() {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <span style={{ color: 'var(--ink-3)', display: 'inline-block' }}>
        <Icon name="inbox" size={26} />
      </span>
      <p style={{ fontWeight: 650, color: 'var(--ink)', marginTop: 8 }}>
        No tienes incidencias abiertas
      </p>
      <p style={{ color: 'var(--ink-2)', marginTop: 4 }}>
        Cuando reportes una incidencia desde Cotalker, aparecerá acá con su avance.
      </p>
    </div>
  )
}
